import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { api } from '../api/client';
import { API_BASE_URL } from '../config/api';
import MenuItemModal from './MenuItemModal/MenuItemModal';
import {
  findMenuItemForCartLine,
  flattenActiveMenuWithItems,
  formatMenuItem,
  menuItemIsCustomisable,
} from '../pages/OurMenu/ourMenuUtils';
import './AlreadyInCartSheet/AlreadyInCartSheet.css';

function useActiveMenuCatalog(enabled) {
  const [menuItems, setMenuItems] = useState([]);
  const [status, setStatus] = useState('idle');

  useEffect(() => {
    if (!enabled) return undefined;
    let cancelled = false;
    setStatus('loading');
    api
      .get(`${API_BASE_URL}/menu/activeMenuWithItems?includeAvailabilityStatus=true`)
      .then((res) => {
        if (cancelled) return;
        // api.get returns { data, requestId, status } — same unwrap as OurMenu.
        const rows = flattenActiveMenuWithItems(res?.data).map(formatMenuItem);
        setMenuItems(rows);
        setStatus('ready');
      })
      .catch(() => {
        if (cancelled) return;
        setMenuItems([]);
        setStatus('error');
      });
    return () => {
      cancelled = true;
    };
  }, [enabled]);

  return { menuItems, status };
}

/**
 * Opens product details in edit mode for a cart line (options / add-ons).
 */
export default function CartCustomiseController({
  cartLine,
  open,
  onClose,
  menuItems = [],
  menuStatus = 'idle',
}) {
  const menuItem = useMemo(
    () => (cartLine ? findMenuItemForCartLine(menuItems, cartLine) : null),
    [menuItems, cartLine],
  );

  const editCartKey = cartLine?.cartKey || null;
  const initialVariantId = useMemo(() => {
    if (!cartLine || !menuItem) return null;
    if (menuItem.kind !== 'variable') return null;
    return Number(cartLine.id);
  }, [cartLine, menuItem]);

  const initialAddOnIds = useMemo(
    () =>
      (cartLine?.addOns || [])
        .map((a) => Number(a.id ?? a.addOnItemID))
        .filter((id) => Number.isFinite(id) && id > 0),
    [cartLine],
  );

  const initialAddOnQtys = useMemo(() => {
    /** @type {Record<number, number>} */
    const map = {};
    for (const a of cartLine?.addOns || []) {
      const id = Number(a.id ?? a.addOnItemID);
      if (!Number.isFinite(id) || id <= 0) continue;
      map[id] = Math.max(1, Math.round(Number(a.quantity) || 1));
    }
    return map;
  }, [cartLine]);

  if (!open || !cartLine) return null;

  if (menuStatus === 'loading' || menuStatus === 'idle') {
    return (
      <div className="aics-overlay" onClick={onClose} role="presentation">
        <div className="aics-sheet cart-customise-error" role="status" onClick={(e) => e.stopPropagation()}>
          <p>Loading customisation…</p>
          <button type="button" className="aics-btn aics-btn--ghost" onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    );
  }

  if (menuStatus === 'error' || !menuItem) {
    return (
      <div className="aics-overlay" onClick={onClose} role="presentation">
        <div className="aics-sheet cart-customise-error" role="alert" onClick={(e) => e.stopPropagation()}>
          <p>
            {menuStatus === 'error'
              ? 'Could not load menu to edit this item. Try again from Our Menu.'
              : 'This item is no longer on the menu.'}
          </p>
          <button type="button" className="aics-btn aics-btn--outline" onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    );
  }

  if (!menuItemIsCustomisable(menuItem)) {
    return (
      <div className="aics-overlay" onClick={onClose} role="presentation">
        <div className="aics-sheet cart-customise-error" role="status" onClick={(e) => e.stopPropagation()}>
          <p>No options or add-ons to change for this item.</p>
          <button type="button" className="aics-btn aics-btn--outline" onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    );
  }

  return (
    <MenuItemModal
      item={menuItem}
      onClose={onClose}
      openIntent="cart"
      editCartKey={editCartKey}
      initialVariantId={initialVariantId}
      initialAddOnIds={initialAddOnIds}
      initialAddOnQtys={initialAddOnQtys}
      initialQuantity={Math.max(1, Number(cartLine.quantity) || 1)}
    />
  );
}

export function cartLineCanCustomise(line, menuItems, menuStatus) {
  if (!line) return false;
  const looksCustomised = Boolean(
    line.parentItemId ||
      line.variantLabel ||
      (Array.isArray(line.addOns) && line.addOns.length > 0),
  );
  if (menuStatus !== 'ready') return looksCustomised;

  const menuItem = findMenuItemForCartLine(menuItems, line);
  if (menuItem) return menuItemIsCustomisable(menuItem);
  // Menu row missing (stale cart) — still allow edit entry if line already has options/add-ons.
  return looksCustomised;
}

export function useCartCustomise(cartItems) {
  const enabled = Array.isArray(cartItems) && cartItems.length > 0;
  const { menuItems, status: menuStatus } = useActiveMenuCatalog(enabled);
  const [editingLine, setEditingLine] = useState(null);
  const openCustomise = useCallback((line) => setEditingLine(line), []);
  const closeCustomise = useCallback(() => setEditingLine(null), []);
  return {
    editingLine,
    openCustomise,
    closeCustomise,
    menuItems,
    menuStatus,
  };
}


